import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import useDispatchStore from "@web-app/store/dispatchStore";
import { AppFilterType, Order } from "@web-app/types";

import { type OrdersCacheData, getOrdersCacheKey } from "../cache/orders";
import { get } from "../services/orders";

import { useSessionUser } from "./useAuth";
import { useOrderApps } from "./useOrderApps";
import { useOrderAssignees } from "./useOrderAssignees";

export const useOrders = (customFilter?: AppFilterType) => {
  const { appFilter: storeFilter } = useDispatchStore();
  const appFilter = customFilter ?? storeFilter;
  const { data: sessionUser } = useSessionUser();
  const userTimezone = sessionUser?.timezone || "system";

  const ordersQuery = useQuery({
    queryFn: async ({ signal }) => {
      return await get({ payload: appFilter, signal });
    },
    queryKey: getOrdersCacheKey(appFilter, userTimezone),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract the orders Map - handle both raw Map and wrapped OrdersCacheData structure
  // (cache updates may store the wrapped structure)
  const ordersMap = useMemo((): Map<string, Order> | undefined => {
    if (!ordersQuery.data) return undefined;
    // Check if it's already wrapped in OrdersCacheData structure
    // Use duck typing to check for the wrapped structure
    const maybeWrapped = ordersQuery.data as unknown as OrdersCacheData;
    if (maybeWrapped.orders instanceof Map) {
      return maybeWrapped.orders;
    }
    // Otherwise it's the raw Map from the query
    return ordersQuery.data;
  }, [ordersQuery.data]);

  // Convert Map to array for useOrderAssignees
  const ordersArray = useMemo(() => {
    if (!ordersMap) {
      return [];
    }
    return Array.from(ordersMap.values());
  }, [ordersMap]);

  // Batch fetch assignees based on order assigneeIds
  const { assigneesMap, isLoading: isAssigneesLoading } =
    useOrderAssignees(ordersArray);

  // Batch fetch apps based on order appIds
  const { appsMap, isLoading: isAppsLoading } = useOrderApps(ordersArray);

  // Combine orders, assignees, and apps into the expected cache data shape
  const data: OrdersCacheData | undefined = useMemo(() => {
    if (!ordersMap) return undefined;
    return {
      apps: appsMap,
      assignees: assigneesMap,
      orders: ordersMap,
    };
  }, [ordersMap, assigneesMap, appsMap]);

  return {
    ...ordersQuery,
    data,
    isAppsLoading,
    isAssigneesLoading,
  };
};
